import { useRef, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, File, FileTextIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import AppTour from "../../../../components/AppTour/AppTour";
import MusicLoader from "../../../../helper/MusicLoader";
import { gettingStartedTourSteps } from "../../../../components/AppTour/ModuleTourSteps";
import { useProfessorUActivityTracker } from "../../../../hooks/useProfessorUActivityTracker";

function Thumbnail({ item }) {
  if (item.type === "icon") {
    const Icon = item.thumbnail;
    return (
      <Icon className="w-[160px] h-[160px] text-theme_clr1" />
    );
  }
  if (item.type === "image" && item.thumbnail == "") {
    return (
      <FileTextIcon className="w-[160px] h-[160px] text-theme_clr1" />
    )
  }
  return (
    <img src={item.thumbnail} alt={item.title} className="w-[250px] h-[200px] object-cover" />
  );
}

export default function LessonsCarousel({ title, lessonsData, pending, showIndexing = true, firstLessonTourEnabled = false }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { hash } = useLocation();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [runTour, setRunTour] = useState(false);
  const queryClient = useQueryClient();
  const { trackLessonCompleted } = useProfessorUActivityTracker();

  useEffect(() => {
    if (firstLessonTourEnabled && title === "Lessons with ProfessorU" && hash === "#getting-started") {
      setRunTour(true);
    }
  }, [firstLessonTourEnabled, title, hash]);

  const handleLessonClick = (item, index) => {
    if (title === "Learn by Module") {
      navigate(`/admin/professor-u/lessons?lesson=${item.title}&moduleId=${item.id}`);
      return;
    }
    if (firstLessonTourEnabled && title === "Lessons with ProfessorU" && index === 0) {
      setRunTour(true);
      return;
    }
    if (title === "Lessons with ProfessorU" && item.url) {
      trackLessonCompleted(item.title, "professoru", item.type);
      navigate(item.url + '#start-tour');
      return;
    }
    navigate(`/admin/professor-u/contents?content=${title}&moduleId=${item.moduleId}&contentId=${item.contentId}`);
  };

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;

    const card = el.querySelector(".snap-start");
    if (!card) return;

    const cardWidth = card.offsetWidth + 16; // 16 = gap-4

    el.scrollBy({
      left: dir === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };

  return (
    <div className="w-full bg-theme_clr1 rounded-2xl p-4 mt-4 relative">
      <AppTour
        tourSteps={gettingStartedTourSteps}
        runTour={runTour}
        setRunTour={setRunTour}
        goBack={false}
        tourName="professoru-lesson-1-getting-started"
        moduleId="professoru"
        activityType="complete_lesson"
        contentId="professoru-lesson-1-getting-started"
      />
      {/* Header */}
      <div className="flex items-center gap-2 text-white mb-6">
        <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
          <img src={require("../../../../assets/images/admin/Amplitude-Info.png")} className="inline w-[30px] h-[30px]" />
          {title}
        </div>
      </div>

      {/* Carousel Row */}
      <div className="flex items-center gap-4">

        {/* LEFT ARROW */}
        <button
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
          className={`flex-shrink-0 transition
            ${canScrollLeft ? "" : "opacity-40 cursor-not-allowed"}
          `}
        >
          <ChevronLeft className="text-white" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto flex-1 no-scrollbar snap-x snap-mandatory px-2 py-2"
        >
          {pending && (
            <div className="flex items-center justify-center w-full min-h-[250px]">
              <MusicLoader />
            </div>
          )}
          {lessonsData.map((item, index) => (
            <div
              key={`${title}-${item.id ?? index}-${index}`}
              onClick={() => handleLessonClick(item, index)}
              className="flex-shrink-0 snap-start w-[242px] h-[250px] bg-gray-200 rounded-xl flex flex-col items-center p-3 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-lg cursor-pointer overflow-visible"
            >
              <div className="flex-1 flex items-center justify-center overflow-hidden w-full rounded-lg">
                <Thumbnail item={item} />
              </div>
              <p
                title={item.title}
                className="w-full min-w-0 text-center text-sm font-normal text-theme_clr1 mt-2 leading-tight px-1 break-words whitespace-normal"
              >
                {showIndexing && index + 1 + "."} {item?.title}
              </p>
            </div>
          ))}
        </div>

        {/* RIGHT ARROW */}
        <button
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
          className={`flex-shrink-0 transition
            ${canScrollRight ? "" : "opacity-40 cursor-not-allowed"}
          `}
        >
          <ChevronRight className="text-white" />
        </button>
      </div>
    </div>
  );
}